import { Component, OnInit, ViewChild, Input, ChangeDetectionStrategy } from '@angular/core';
import { ModalController, AlertController, LoadingController, IonInput } from '@ionic/angular/lazy';
import { ObserverService } from 'src/app/services/observer.service';
import { APIService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { SupportedCurrencyService } from 'src/app/services/supported-currency.service';
import { StorageService } from 'src/app/services/storage.service';
import { ConnectedAccountService } from 'src/app/services/connected-account.service';
import { RegisterService } from 'src/app/services/register.service';
import { Router } from '@angular/router';
import { Decimal } from 'decimal.js';

@Component({
    selector: 'app-organisation-add-store-credit',
    templateUrl: './organisation-add-store-credit.page.html',
    styleUrls: ['./organisation-add-store-credit.page.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OrganisationAddStoreCreditPage implements OnInit {

    @ViewChild('amount_input', { static: false }) amount_input: IonInput;

    spinner = null;
    loading_spinner = null;
    loading = false;
    online = true;
    register_id = null;
    register = null;
    @Input() organisation_id = null;
    organisation = null;
    currency_id = null;
    supported_currencies = null;
    settings = null;
    xero_connected = false;

    params = {
        type: "add",
        amount: null,
        internal_note: null,
        add_xero_manual_journal: false
    };

    currency_options: any = { header: 'Currency' };

    constructor(
        private modalController: ModalController,
        private alertController: AlertController,
        private loadingController: LoadingController,
        private router: Router,
        private apiService: APIService,
        private authService: AuthService,
        private observerService: ObserverService,
        private supportedCurrencyService: SupportedCurrencyService,
        private storageService: StorageService,
        private connectedAccountService: ConnectedAccountService,
        private registerService: RegisterService
    ) { }

    ngOnInit() {

        if(this.organisation_id) {


            this.loadData().then(() => {

                if(this.supported_currencies.length > 1) {

                    this.storageService.get("register_id").then((register_id) => {

                        if(register_id) {

                            this.register_id = register_id;

                            this.registerService.getRegister(this.register_id).then((register) => {

                                if(register) {

                                    this.register = register;

                                    for(let i = 0; i < this.supported_currencies.length; i++) {

                                        // NOTE: default currency
                                        if(this.supported_currencies[i].id == this.register['location'].default_currency.id) {
                                            let register_location_default_currency = this.supported_currencies.splice(i, 1)[0];
                                            this.supported_currencies.unshift(register_location_default_currency);
                                            this.currency_id = register_location_default_currency.id;
                                            break;
                                        }

                                    }
                                }
                            })
                            .catch(() => { });
                        }
                    })
                    .catch(() => { });

                // NOTE: if only one currency is supported, select it automatically
                }
                else {
                    this.currency_id = this.supported_currencies[0].id;
                }

                if(this.settings.xero_connected == "true") {
                    this.xero_connected = true;
                    this.params.add_xero_manual_journal = true;
                }

            }).catch(() => { });

        }

        this.observerService.subscribe('supported_currencies_changed|organisation_add_store_credit_modal', supported_currencies => {
            this.supported_currencies = supported_currencies;
        });

        this.observerService.subscribe('supported_currency_added|organisation_add_store_credit_modal', (supported_currency) => {
            if(supported_currency) {
                this.supported_currencies.push(supported_currency);
            }
        });

        this.observerService.subscribe('supported_currency_deleted|organisation_add_store_credit_modal', (supported_currency) => {
            if(supported_currency) {
                for(let i = 0; i < this.supported_currencies.length; i++) {
                    if(this.supported_currencies[i].id === supported_currency["id"]) {
                        this.supported_currencies.splice(i, 1);
                        break;
                    }
                }
            }
        });

        this.observerService.subscribe('network_status_changed|organisation_add_store_credit_modal', (network_status) => {
            if(network_status) {
                this.online = network_status.connected;
                if(this.online) {
                    this.loadData().then(() => { }).catch(() => { });
                }
                else {
                    this.presentAlert("Offline", "You must be online to add store credit, please check your network connection and try again.");
                }
            }
        });

    }

    loadData() {
        return new Promise<void>((resolve, reject) => {
            this.presentLoadingSpinner("Loading organisation").then(() => {
                this.loadOrganisation().then(() => {
                    this.loading_spinner.message = "Loading currencies";
                    this.loadSupportedCurrencies().then(() => {
                        this.loading_spinner.message = "Loading settings";
                        this.loadSettings().then(() => {
                            this.loading_spinner.dismiss();
                            resolve();
                        })
                        .catch(() => {
                            this.loading_spinner.dismiss();
                            reject();
                        });
                    })
                    .catch(() => {
                        this.loading_spinner.dismiss();
                        reject();
                    });
                })
                .catch(() => {
                    this.loading_spinner.dismiss();
                    reject();
                });
            });
        });
    }

    ngOnDestroy() {
        this.observerService.destroy("supported_currencies_changed|organisation_add_store_credit_modal");
        this.observerService.destroy("supported_currency_added|organisation_add_store_credit_modal");
		this.observerService.destroy("supported_currency_deleted|organisation_add_store_credit_modal");
        this.observerService.destroy("network_status_changed|organisation_add_store_credit_modal");
    }

    ionViewDidEnter() {
        // NOTE: we use a timeout here so the DOM gets a chance to load before setting focus
        setTimeout(() => {
            if(this.amount_input) {
                this.amount_input.setFocus();
            }
        }, 200);
    }

    loadOrganisation() {
        return new Promise<void>((resolve, reject) => {
            this.organisation = null;
            this.authService.isLoggedIn().then(() => {
                this.apiService.getOrganisation(this.organisation_id).then((organisation: any) => {
                    if(organisation) {
                        this.organisation = organisation;
                    }
                    resolve();
                })
                .catch((error) => {
                    this.organisation = {};
                    if(error.status == 400) {
                        this.presentAlert("Unknown error", "Unfortunately something went wrong when trying to retrieve an organisation.");
                    }
                    else if(error.status == 403) {
                        this.presentAlert("Insufficient permissions", "You don't have permission to view an organisation.");
                    }
                    else if(error.status == 404) {
                        this.presentAlert("Unavailable", "Unfortunately we're unable to retrieve an organisation at this time.");
                    }
                    else if(error.status == 0) {
                        this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    loadSupportedCurrencies() {
        return new Promise<void>((resolve, reject) => {
            this.supportedCurrencyService.getSupportedCurrencies().then((supported_currencies: any) => {
                if(supported_currencies) {
                    supported_currencies.sort((a, b) => {
                        if (a['title'].toLowerCase() < b['title'].toLowerCase()) { return -1; }
                        if (a['title'].toLowerCase() > b['title'].toLowerCase()) { return 1; }
                        return 0;
                    });
                    this.supported_currencies = supported_currencies;
                }
                resolve();
            })
            .catch(() => {
                reject();
            });
        })
    }

    loadSettings() {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.storageService.get("connected_account_id").then(connected_account_id => {
					if (connected_account_id) {
						this.connectedAccountService.getConnectedAccount(connected_account_id).then(connected_account => {
                            if (connected_account) {
                                this.apiService.getSettings(connected_account).then((settings: {}) => {
                                    if(settings) {
                                        this.settings = settings;
                                    }
                                    resolve();
                                })
                                .catch((error) => {
                                    if(error.status === 400) {
                                        this.presentAlert('Unknown error', "Unfortunately something went wrong when trying to retrieve the settings.");
                                    }
                                    else if(error.status == 403) {
                                        this.presentAlert('Insufficient permissions', "You don't have permission to view settings.");
                                    }
                                    else if(error.status == 404) {
                                        this.presentAlert('Unavailable', "Unfortunately we're unable to retrieve settings at this time.");
                                    }
                                    else if(error.status == 0) {
                                        this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                                    }
                                    reject();
                                });
                            }
                            else {
                                reject();
                            }
                        })
                        .catch(error => {
                            reject(error);
                        });
					}
                    else {
						reject();
					}
				})
				.catch(error => {
					reject(error);
				});
            })
            .catch(() => {
                this.router.navigate(['switch-account'], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    close() {
        this.modalController.dismiss();
    }

    add() {

        let validated = true;
        let price_regex = /^\d+(\.\d+)?$/;

        // catch empty currency
        if(this.currency_id == null || this.currency_id.trim() === "") {
            this.presentAlert("Missing currency", "You need to set a currency.");
            validated = false;
        }
        // catch empty amount
        else if(this.params.amount == null || String(this.params.amount).trim() === "") {
            this.presentAlert("Missing amount", "You need to set an amount.");
            validated = false;
        }
        // catch letters or symbols or multiple dps in amount
        else if(!price_regex.test(String(this.params.amount).toLowerCase())) {
            this.presentAlert("Invalid amount", "The amount is invalid.");
            validated = false;
        }
        // catch empty internal note
        else if(this.params.internal_note == null || this.params.internal_note.trim() === "") {
            this.presentAlert("Missing internal note", "You need to enter an internal note.");
            validated = false;
        }

        if(validated) {

            // ensure 2dp
            this.params.amount = Number(new Decimal(this.params.amount).toDecimalPlaces(2));

            this.addOrganisationStoreCredit().then(async () => {
                await this.modalController.dismiss();
            })
            .catch(() => {});

        }
    }

    addOrganisationStoreCredit() {
        return new Promise<void>((resolve, reject) => {
            this.presentSpinner("Adding store credit").then(() => {
                this.authService.isLoggedIn().then(() => {
                    this.apiService.updateOrganisationStoreCredit(this.organisation_id, this.currency_id, this.params).then((store_credit) => {
                        this.observerService.publish('organisation_store_credit_updated', this.organisation_id);
                        this.spinner.dismiss();
                        resolve();
                    })
                    .catch((error) => {
                        if(error) {
                            if(error.status === 400) {
                                this.presentAlert("Unknown error", "Unfortunately something went wrong when trying to add store credit.");
                            }
                            else if(error.status === 403) {
                                this.presentAlert("Insufficient permissions", "You don't have permission to add store credit.");
                            }
                            else if(error.status == 404) {
                                this.presentAlert("Unavailable", "Unfortunately we're unable to add store credit at this time.");
                            }
                            else if(error.status == 0) {
                                this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                            }
                        }
                        this.spinner.dismiss();
                        reject();
                    });
                })
                .catch(() => {
                    this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                        window.location.reload();
                    });
                });
            });
        });
    }

    async presentAlert(header, message) {
        const alert = await this.alertController.create({
            header: header,
            message: message,
            cssClass:'alert',
            buttons: ['OK']
        });
        await alert.present();
    }

    async presentSpinner(message) {
        this.spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
        });
        await this.spinner.present();
    }

    async presentLoadingSpinner(message) {
        this.loading = true;
        this.loading_spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        this.loading_spinner.onDidDismiss().then(() => {
            this.loading = false;
        });
        await this.loading_spinner.present();
    }

}
